"use client";

const IMG_BASE_URL = "https://d37pbainmkhx6o.cloudfront.net/";

/**
 * Metadata returned by the backend's /request-bucket-url endpoint
 */
interface PresignedUrlResponse {
  status: string;
  uploadURL: string;
  fileservername: string;
  added_time: number;
  filelocalname: string;
}

/**
 * Standard S3 image metadata structure expected by the backend
 */
export interface S3ImageMetadata {
  fileservername: string;
  name: string;
  size: number;
  type: string;
  path: string;
  bucket: string;
  baseurl: string;
  imgUrl: string;
}

/**
 * Utility to upload a file to S3 via a presigned URL
 */
export async function uploadToS3(
  file: File,
  path: string,
  bucket: string,
  token: string
): Promise<S3ImageMetadata> {
  const baseUrl =
    process.env.NEXT_PUBLIC_API_CLOUDFRONT || "https://d36fwfwo4vnk9h.cloudfront.net/";

  // 1. Request presigned URL from backend
  const requestRes = await fetch(`${baseUrl}user-profile/request-bucket-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      name: file.name,
      type: file.type,
      path,
      bucket,
    }),
  });

  if (!requestRes.ok) {
    throw new Error(`Failed to request S3 upload URL: ${requestRes.statusText}`);
  }

  const presignedData: PresignedUrlResponse = await requestRes.json();
  if (presignedData.status !== "success") {
    throw new Error("Backend failed to provide S3 upload URL");
  }

  // 2. PUT file directly to S3
  const s3Res = await fetch(presignedData.uploadURL, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": file.type,
    },
  });

  if (!s3Res.ok) {
    throw new Error(`S3 upload failed: ${s3Res.statusText}`);
  }

  // 3. Construct and return final metadata
  return {
    fileservername: presignedData.fileservername,
    name: file.name,
    size: file.size,
    type: file.type,
    path,
    bucket,
    baseurl: path,
    imgUrl: `${IMG_BASE_URL}${presignedData.fileservername}`,
  };
}
